import { Button, Grid, IconButton, Typography, TextField } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import React, { useEffect, useState, useMemo } from 'react';
import Navbar from '../../components/Navbar';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import DataDisplay from '../../components/DataDisplay';
import { API } from '../../api/api_setup';
import ClusterSwitch from '../../components/ClusterSwitch';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useAuth } from '../../hooks/useAuth';
import { useDispatch, useSelector } from 'react-redux';
import { setClusters, updateCluster } from './clusterSlice'

const Clusters = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [disableBtn, setDisableBtn] = useState(true)
  const [searchTerm, setSearchTerm] = useState('All')
  const clusters = useSelector(state => state.cluster.clusters)
  const dispatch = useDispatch()
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth()
  const navigate = useNavigate()
  const handleClusterStateChange = ({
    clusterName,
    fieldName,
    fieldValue
  }) => {
    setDisableBtn(false)
    dispatch(updateCluster({clusterName, fieldName, fieldValue}))
  }
  const handleSaveChanges = () => {
    API.patch('/cluster/patch', clusters, {
      headers: {
        'Authorization': `Bearer ${user.token}`
      }
    })
      .then((result) => {
        showSnackbar('Updated !!')
        setDisableBtn(true)
      })
      .catch(err => {
        showSnackbar('Something went wrong !!')
      })
  }

  useEffect(() => {
    if (clusters.length > 0) {
      setIsLoading(false)
      return
    }
    API.get('/cluster/info', {
      headers: {
        'Authorization': `Bearer ${user.token}`
      }
    })
      .then((result) => {
        dispatch(setClusters(result.data.data))
        setIsLoading(false)
      })
      .catch(err => {
        showSnackbar("Something went wrong !!")
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clusterNameOptions = useMemo(() => {
    const unique = new Set()
    clusters.forEach((cluster) => {
      if (cluster?.clusterName) {
        unique.add(cluster.clusterName)
      }
    })
    return ['All', ...Array.from(unique).sort()]
  }, [clusters])

  const filteredClusters = useMemo(() => {
    const term = (searchTerm || '').trim().toLowerCase()
    if (term === '' || term === 'all') {
      return clusters
    }
    return clusters.filter((cluster) => {
      const value = String(cluster?.clusterName || '').toLowerCase()
      return value.includes(term)
    })
  }, [clusters, searchTerm])

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option || ''
  })

  const clusterColumns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 50
    },
    {
      field: 'clusterName',
      headerName: 'Cluster name',
      width: 300,
    },
    {
      field: 'isInternal',
      headerName: 'Internal Cluster',
      width: 150,
      renderCell: (cellValue) => {
        return <ClusterSwitch cellValue={cellValue} handleClusterStateChange={handleClusterStateChange} fieldName={'isInternal'}/>
      }
    },
    {
      field: 'isProduction',
      headerName: 'Production Cluster',
      width: 150,
      renderCell: (cellValue) => {
        return <ClusterSwitch cellValue={cellValue} handleClusterStateChange={handleClusterStateChange} fieldName={'isProduction'}/>
      }
    },
    {
      field: 'isOwned',
      headerName: 'Owned Cluster',
      width: 150,
      renderCell: (cellValue) => {
        return <ClusterSwitch cellValue={cellValue} handleClusterStateChange={handleClusterStateChange} fieldName={'isOwned'}/>
      }
    }
  ]

  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={12} md={12}>
          <Navbar leftWidgetList={[
            <IconButton
              size="large"
              color="inherit"
              aria-label="home"
              onClick={() => { navigate("/") }}
              sx={{ marginLeft: '-10px' }}
              key={0}
            >
              <HomeIcon sx={{ fontSize: 40 }} />
            </IconButton>,
            <Typography variant='h4' sx={{ marginLeft: '10px' }} key={1}>{"Clusters"}</Typography>
          ]}
            rightWidgetList={[
              <Button
                variant='contained'
                color='secondary'
                onClick={handleSaveChanges}
                key={0}
                disabled={disableBtn}
              >Apply Changes</Button>
            ]}
          />
        </Grid>
        <Grid item lg={12} md={12}>
          <Grid container alignItems={'center'} sx={{ paddingLeft: '10px', paddingRight: '10px', paddingTop: '10px' }}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                options={clusterNameOptions}
                value={searchTerm}
                inputValue={searchTerm}
                onChange={(event, newValue) => setSearchTerm(newValue ?? '')}
                onInputChange={(event, newInputValue) => setSearchTerm(newInputValue ?? '')}
                freeSolo
                filterOptions={filterOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cluster name"
                    size='small'
                  />
                )}
                filterSelectedOptions
                disableClearable
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={12} md={12} sx={{ maxHeight: '90vh', minHeight: '20vh' }}>
          <DataDisplay
            rows={filteredClusters.map((item, index) => ({ ...item, id: index }))}
            columns={clusterColumns}
            loading={isLoading}
            sx={{ margin: '10px' }}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default Clusters;
